import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { Input } from '@/components/ui/Input'
import { usePastRendezVous } from '@/hooks/useAgenda'
import {
  ChevronDown,
  ChevronUp,
  Calendar as CalendarIcon,
  History,
} from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

const statutConfig: Record<string, { label: string; color: 'mint' | 'gold' | 'brown' }> = {
  en_attente: { label: 'En attente', color: 'gold' },
  confirme: { label: 'Confirmé', color: 'mint' },
  annule: { label: 'Annulé', color: 'brown' },
  termine: { label: 'Terminé', color: 'brown' },
}

export function PastAppointments() {
  const [isOpen, setIsOpen] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const limit = 10

  const { data, isLoading } = usePastRendezVous(
    isOpen ? { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, page, limit } : undefined
  )

  const totalPages = data ? Math.ceil(data.total / limit) : 0

  return (
    <Card>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between"
      >
        <h2 className="text-lg font-semibold text-brown-900 flex items-center gap-2">
          <History className="w-5 h-5 text-brown-500" />
          Historique des rendez-vous
          {data && (
            <span className="text-sm font-normal text-brown-500">
              ({data.total})
            </span>
          )}
        </h2>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-brown-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-brown-500" />
        )}
      </button>

      {isOpen && (
        <div className="mt-4">
          {/* Filtres */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-brown-600 whitespace-nowrap">Du</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value)
                  setPage(1)
                }}
                className="!w-auto"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-brown-600 whitespace-nowrap">Au</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value)
                  setPage(1)
                }}
                className="!w-auto"
              />
            </div>
            {(dateFrom || dateTo) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDateFrom('')
                  setDateTo('')
                  setPage(1)
                }}
              >
                Effacer
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : !data?.rendezVous.length ? (
            <p className="text-center text-brown-500 py-8">Aucun rendez-vous passé</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-brown-200">
                      <th className="px-4 py-3 text-left text-xs font-medium text-brown-600 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-brown-600 uppercase">
                        Client
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-brown-600 uppercase">
                        Description
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-brown-600 uppercase">
                        Statut
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brown-100">
                    {data.rendezVous.map((rdv) => (
                      <tr key={rdv.id} className="hover:bg-brown-50 transition">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-sm">
                            <CalendarIcon className="w-4 h-4 text-brown-400" />
                            <span className="text-brown-900">
                              {format(new Date(rdv.date_heure), 'Pp', { locale: fr })}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-brown-900">
                            {rdv.client?.full_name || rdv.client?.email || 'Client'}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-brown-600 truncate max-w-[200px]">
                            {rdv.description || '-'}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge color={statutConfig[rdv.statut]?.color || 'brown'}>
                            {statutConfig[rdv.statut]?.label || rdv.statut}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-beige-200">
                  <p className="text-sm text-brown-500">
                    Page {page} sur {totalPages} ({data.total} résultats)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      Précédent
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  )
}
